import type { TokenStore } from '@finmin/api-client';

/**
 * FinMin's deliberate token-storage split (do not change without reading why):
 *
 *   - access token     → sessionStorage (tab-scoped, dies when tab closes)
 *   - refresh token    → localStorage   (cross-tab persistence so a new tab
 *                                        on the same browser stays logged in)
 *   - firestore token  → sessionStorage (used by chat component to talk to
 *                                        Firestore directly; minted by the
 *                                        backend on every login/refresh)
 *
 * Rationale: short-lived access tokens shouldn't outlive the tab session,
 * but the refresh token needs to live long enough to silently re-auth a
 * second tab. SSR-safe (no-ops on the server).
 */

const ACCESS_KEY = 'finmin.accessToken';
const REFRESH_KEY = 'finmin.refreshToken';
const FIRESTORE_KEY = 'finmin.firestoreToken';

const isBrowser = typeof window !== 'undefined';

export function createTokenStore(): TokenStore {
  return {
    getAccessToken() {
      return isBrowser ? sessionStorage.getItem(ACCESS_KEY) : null;
    },
    getRefreshToken() {
      return isBrowser ? localStorage.getItem(REFRESH_KEY) : null;
    },
    getFirestoreToken() {
      return isBrowser ? sessionStorage.getItem(FIRESTORE_KEY) : null;
    },
    setTokens({ accessToken, refreshToken, firestoreToken }) {
      if (!isBrowser) return;
      sessionStorage.setItem(ACCESS_KEY, accessToken);
      if (refreshToken !== undefined) {
        localStorage.setItem(REFRESH_KEY, refreshToken);
      }
      if (firestoreToken !== undefined) {
        if (firestoreToken === null) sessionStorage.removeItem(FIRESTORE_KEY);
        else sessionStorage.setItem(FIRESTORE_KEY, firestoreToken);
      }
    },
    clear() {
      if (!isBrowser) return;
      sessionStorage.removeItem(ACCESS_KEY);
      sessionStorage.removeItem(FIRESTORE_KEY);
      localStorage.removeItem(REFRESH_KEY);
    },
  };
}
